import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Tag, Snowflake, Truck, X, ArrowRight } from 'lucide-react';
import { useT } from '../services/i18n';
import { useLangSuggest } from './LanguageSuggestBanner';
import { usePromoBanner, PromoCode } from './PromoBanner';
import { useWinterBanner } from './WinterTyresBanner';
import { useShippingSummary, fmtEuro } from '../services/shippingInfo';

const ROTATE_MS = 5000;

// Smartphone-only: language / promo / winter / free shipping in one slim rotating strip
const MobileTopStrip = () => {
  const { t, lang } = useT();
  const shipInfo = useShippingSummary();
  const ls = useLangSuggest();
  const promo = usePromoBanner();
  const winter = useWinterBanner();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  const items = [];
  if (ls) items.push({ key: 'lang', cls: 'bg-slate-900 text-white', icon: <Globe size={14} className="text-orange-400 shrink-0" />,
    body: <><span className="line-clamp-2 leading-tight">{ls.txt.msg}</span><button type="button" onClick={ls.accept} data-testid="strip-lang-accept" className="shrink-0 font-semibold text-orange-400 underline underline-offset-2 min-h-[32px] px-1">{ls.txt.yes}</button></>, close: ls.close });
  if (promo) items.push({ key: 'promo', cls: 'bg-orange-500 text-white', icon: <Tag size={14} className="shrink-0" />,
    body: <><span className="line-clamp-2 leading-tight">{promo.banner.text_i18n?.[lang] || promo.banner.text}</span><PromoCode {...promo} t={t} /></> });
  if (winter) items.push({ key: 'winter', cls: 'bg-sky-950 text-sky-50', icon: <Snowflake size={14} className="text-sky-300 shrink-0" />,
    body: <><Link to={winter.banner.link} className="line-clamp-2 leading-tight">{winter.banner.text_i18n?.[lang] || winter.banner.text}</Link><Link to={winter.banner.link} data-testid="strip-winter-cta" aria-label={winter.cta} title={winter.cta} className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-sky-300 text-sky-950"><ArrowRight size={14} /></Link></>, close: winter.close });
  items.push({ key: 'shipping', cls: 'bg-black text-gray-300', icon: <Truck size={14} className="text-orange-400 shrink-0" />, body: <span className="line-clamp-2 leading-tight">{shipInfo.freeFrom != null ? t('header.freeShipping', { n: fmtEuro(shipInfo.freeFrom).replace(' €', '') }) : t('header.fastShipping')}</span> });

  const n = items.length;
  const cur = Math.min(idx, n - 1);
  useEffect(() => {
    if (n < 2 || paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % n), ROTATE_MS);
    return () => clearInterval(id);
  }, [n, paused]);

  const item = items[cur];
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; setPaused(true); };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchX.current ?? e.changedTouches[0].clientX);
    if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : n - 1)) % n);
    setPaused(false);
  };

  return (
    <div className={`md:hidden text-xs transition-colors duration-500 ${item.cls}`} data-testid="mobile-top-strip" data-current={item.key} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} aria-live="polite">
      <div className="px-3 min-h-9 py-1 flex items-center gap-2">
        {item.icon}
        <div key={item.key} className="flex-1 min-w-0 flex items-center gap-2 animate-in fade-in slide-in-from-bottom-1 duration-300" data-testid={`strip-item-${item.key}`}>{item.body}</div>
        {n > 1 && (
          <div className="flex items-center gap-1 shrink-0" data-testid="strip-dots">
            {items.map((it, i) => <button key={it.key} type="button" aria-label={it.key} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === cur ? 'w-3.5 bg-white' : 'w-1.5 bg-white/40'}`} />)}
          </div>
        )}
        {item.close && <button type="button" onClick={() => { item.close(); setIdx(0); }} aria-label="Fermer" data-testid={`strip-close-${item.key}`} className="shrink-0 inline-flex items-center justify-center w-8 h-8 -mr-2 rounded-full text-white/70 hover:bg-white/10"><X size={14} /></button>}
      </div>
    </div>
  );
};

export default MobileTopStrip;
